import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { Subscription, filter } from 'rxjs';
import { BillingService, CompanyBilling } from 'src/app/services/billing.service';
import { CoreService } from 'src/app/services/core.service';
import { TPipe } from 'src/app/i18n/t.pipe';

/**
 * Notice at the top of every page for the owner of a company that signed up by itself: payment not finished
 * (only on the pages that stay open), subscription canceled, or a failed charge. Companies from before (not billed
 * here) never see it. The status is read again on each full page load, so a change made by Stripe shows up.
 */
@Component({
  selector: 'app-billing-banner',
  standalone: true,
  imports: [CommonModule, RouterModule, TPipe],
  template: `
    @if (b && show) {
      @if (b.status === 'PastDue') {
        <div class="bb bb--error" role="alert" data-test="banner-pastdue">
          <span>{{ 'bb.pastDue' | t }}</span>
          <button type="button" class="bb-btn bb-btn--error" (click)="portal()" [disabled]="opening">{{ (opening ? 'bb.opening' : 'bb.update') | t }}</button>
        </div>
      } @else if (b.status === 'Incomplete' && !onSignup) {
        <div class="bb bb--warn" role="status" data-test="banner-incomplete">
          <span>{{ 'bb.incomplete' | t: { company: b.companyName || '' } }}</span>
          <a class="bb-btn bb-btn--warn" routerLink="/apps/register-company-owner">{{ 'bb.finish' | t }}</a>
        </div>
      } @else if (b.status === 'Canceled' && !onSignup) {
        <div class="bb bb--warn" role="status" data-test="banner-canceled">
          <span>{{ 'bb.canceled' | t }}</span>
          <a class="bb-btn bb-btn--warn" routerLink="/apps/register-company-owner">{{ 'bb.pick' | t }}</a>
        </div>
      }
    }
  `,
  styles: [`
    .bb { display: flex; gap: 14px; align-items: center; flex-wrap: wrap; border-radius: 14px; padding: 14px 16px; margin: 0 0 16px; font-size: 14px; line-height: 1.5; }
    .bb span { flex: 1 1 320px; }
    .bb--warn { background: #fff7ed; border: 1px solid #fdba74; color: #7c2d12; }
    .bb--error { background: #fef2f2; border: 1px solid #fca5a5; color: #7f1d1d; }
    .bb-btn { display: inline-flex; align-items: center; height: 42px; padding: 0 16px; border-radius: 10px; color: #fff; font-weight: 600; font-size: 14px; border: 0; cursor: pointer; text-decoration: none; }
    .bb-btn--warn { background: #b45309; }
    .bb-btn--error { background: #b91c1c; }
    .bb-btn:disabled { opacity: .7; }
    .bb-btn:focus-visible { outline: 2px solid #1d4ed8; outline-offset: 2px; }
    :host-context(.dark-theme) .bb--warn { background: rgba(251,146,60,.12); border-color: #9a3412; color: #fdba74; }
    :host-context(.dark-theme) .bb--error { background: rgba(248,113,113,.12); border-color: #991b1b; color: #fca5a5; }
  `]
})
export class BillingBannerComponent implements OnInit, OnDestroy {
  private api = inject(BillingService);
  private core = inject(CoreService);
  private router = inject(Router);

  b: CompanyBilling | null = null;
  onSignup = false;
  opening = false;
  private sub?: Subscription;

  /** Owners only (the billing page is theirs). */
  get show(): boolean {
    return this.core.getRole() === 'CompanyOwner';
  }

  ngOnInit(): void {
    this.onSignup = this.isSignup(this.router.url);
    this.sub = this.router.events.pipe(filter(e => e instanceof NavigationEnd)).subscribe((e: any) => {
      this.onSignup = this.isSignup(e.urlAfterRedirects ?? e.url);
    });

    // Only companies billed through sign-up have a status saved at login; the others are never billed here.
    if (this.show && this.core.getHasCompany() && this.core.getBillingStatus()) {
      this.api.status().subscribe({
        next: b => {
          this.b = b.managed ? b : null;
          this.api.remember(null, b.status);
        },
        error: () => { this.b = null; }
      });
    }
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  private isSignup(url: string): boolean {
    return (url || '').split('?')[0].startsWith('/apps/register-company-owner');
  }

  portal(): void {
    if (this.opening) return;
    this.opening = true;
    this.api.portal().subscribe({
      next: r => window.location.assign(r.url),
      error: () => { this.opening = false; }
    });
  }
}
